import {
  type ILearningObjectInstanceResponse,
  type ILearningObjectNoteResponse,
  type ILearningObjectResourceGradeResponse,
  type ILearningObjectResourceResponse,
  type ILearningObjectResponse,
  learningObjectInstanceType,
  learningObjectNoteType,
  learningObjectResourceGradeType,
  learningObjectResourceType,
  learningObjectType
} from '@/services';
import { enrollmentType, type IEnrollmentResponse } from '@/services/prime/enrollment';
import { type IResourceResponse, resourceType } from '@/services/prime/resource';

export interface IPrimeResponse<T, R = undefined> {
  data?: T;
  isSuccess?: boolean;
  included?: R;
  links?: {
    self: string;
    next?: string;
  };
  meta?: {
    count?: number;
    formalCount?: number;
    informalCount?: number;
  };
}

export interface IPrimeDefaultObject {
  id: string;
  type: string;
  relationships?: IRelationships;
  attributes?: {
    [key: string]: string | string[] | number | boolean | undefined | ILocalizedMetadata[];
  };
}

export interface IPrimeParams {
  [key: string]: string | number | boolean | string[];
}

export type LocaleEnum =
  | 'en-US'
  | 'fr-FR'
  | 'de-DE'
  | 'zh-CN'
  | 'es-ES'
  | 'it-IT'
  | 'ja-JP'
  | 'pt-BR'
  | 'da-DK'
  | 'hi-IN'
  | 'hu-HU'
  | 'nl-NL'
  | 'pl-PL'
  | 'fi-FI'
  | 'th-TH'
  | 'tr-TR'
  | 'ko-KR'
  | 'zh-TW'
  | 'no-NO'
  | 'sv-SE'
  | 'ro-RO'
  | 'ar-SA'
  | 'ru-RU'
  | 'am-ET'
  | 'cs-CZ';

export interface IRelationships {
  authors?: IMultiLink;
  enrollment?: ISingleLink;
  instances?: IMultiLink;
  skills?: IMultiLink;
  subLOs?: IMultiLink;
  account?: ISingleLink;
  manager?: ISingleLink;
  learningObject?: ISingleLink;
  loInstance?: ISingleLink;
  resources?: IMultiLink;
  loResource?: ISingleLink;
  loResources?: IMultiLink;
  badge?: ISingleLink;
  learner?: ISingleLink;
  model?: ISingleLink;
  skill?: ISingleLink;
  skillLevel?: ISingleLink;
  supplementaryResources?: IMultiLink;
  subLoInstances?: IMultiLink;
}

export interface IMultiLink {
  data: ILinkData[];
}

export interface ILinkData {
  id: string;
  type: string;
}

export interface ISingleLink {
  data: ILinkData;
}

export interface ILocalizedMetadata {
  description?: string;
  locale?: LocaleEnum;
  name?: string;
  overview?: string;
}

export type IncludedType =
  | IEnrollmentResponse
  | IResourceResponse
  | ILearningObjectResourceResponse
  | ILearningObjectResponse
  | ILearningObjectResourceGradeResponse
  | ILearningObjectInstanceResponse
  | ILearningObjectNoteResponse;

export const guards = {
  isLearningObject: (item: IncludedType): item is ILearningObjectResponse => item.type == learningObjectType,
  isEnrollment: (item: IncludedType): item is IEnrollmentResponse => item.type === enrollmentType,
  isGrade: (item: IncludedType): item is ILearningObjectResourceGradeResponse =>
    item.type === learningObjectResourceGradeType,
  isInstance: (item: IncludedType): item is ILearningObjectInstanceResponse => item.type === learningObjectInstanceType,
  isResource: (item: IncludedType): item is ILearningObjectResourceResponse => item.type === learningObjectResourceType,
  isResourceData: (item: IncludedType): item is IResourceResponse => item.type === resourceType,
  isNote: (item: IncludedType): item is ILearningObjectNoteResponse => item.type === learningObjectNoteType
};
